import React from 'react';
import { Menu, Bell, User, LogOut, Sun, Moon, Monitor, Wrench } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../common/Button';
import { ShortcutBar } from '../common/ShortcutBar';
import { WarehouseIndicator } from '../common/WarehouseIndicator';
import { MaintenanceAdminModal } from '../common/MaintenanceAdminModal';

interface HeaderProps {
    title: string;
    onMenuClick: () => void;
    onProfileClick: () => void;
    onHomeClick: () => void;
    onNavigate: (path: string) => void;
}

export const Header: React.FC<HeaderProps> = ({ title, onMenuClick, onProfileClick, onHomeClick, onNavigate }) => {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [showMaintenanceModal, setShowMaintenanceModal] = React.useState(false);

    return (
        <>
        <header className="bg-white border-b border-gray-200 px-2 sm:px-4 py-2 sm:py-3 flex items-center justify-between sticky top-0 z-30 shadow-sm">
            {/* Left section: Menu + Logo + Title */}
            <div className="flex items-center gap-1 sm:gap-3 min-w-0 flex-shrink-0">
                <button
                    onClick={onMenuClick}
                    className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
                >
                    <Menu size={20} />
                </button>
                <button
                    onClick={onHomeClick}
                    className="p-1 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
                    title="Gå til Dashboard"
                >
                    <img src="/compass-logo.png" alt="Compass" className="w-8 h-8" />
                </button>
            </div>

            {/* Shortcut bar in the middle - hidden on mobile */}
            <div className="hidden md:flex flex-1 mx-4">
                <ShortcutBar onNavigate={onNavigate} />
            </div>

            {/* Right section: User info + Actions */}
            <div className="flex items-center gap-1 sm:gap-2 md:gap-4">
                {/* Warehouse indicator - read-only badge */}
                <WarehouseIndicator />

                {/* Maintenance mode - only visible for level 3 (consultant) */}
                {user?.operatorGroupId === '3' && (
                    <button
                        onClick={() => setShowMaintenanceModal(true)}
                        className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
                        title="Vedlikeholdsmodus"
                    >
                        <Wrench size={20} className="text-gray-600" />
                    </button>
                )}

                {/* Theme toggle: light → grey → dark → light */}
                <button
                    onClick={toggleTheme}
                    className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors"
                    title={theme === 'light' ? 'Bytt til grått tema' : theme === 'grey' ? 'Bytt til mørkt tema' : 'Bytt til lyst tema'}
                >
                    {theme === 'dark' ? <Sun size={20} className="text-amber-400" /> : theme === 'grey' ? <Monitor size={20} className="text-gray-500" /> : <Moon size={20} className="text-gray-600" />}
                </button>

                {/* User info - hidden on mobile, compact on tablet, full on desktop */}
                {user && (
                    <div className="hidden lg:block text-right text-sm">
                        <div className="font-medium text-gray-800">{user.operatorName}</div>
                        <div className="text-gray-500 text-xs">
                            {user.tenantName && <span>{user.tenantName} · </span>}
                            {user.companyNo} / {user.customerNo}
                        </div>
                    </div>
                )}

                {/* Bell - hidden on small mobile */}
                <button className="hidden sm:flex p-2 min-w-[44px] min-h-[44px] items-center justify-center hover:bg-gray-100 rounded-lg transition-colors">
                    <Bell size={20} className="text-gray-600" />
                </button>

                {/* Profile button */}
                <button
                    onClick={onProfileClick}
                    className="w-10 h-10 sm:w-8 sm:h-8 min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px] bg-blue-500 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
                    title="Vis profil"
                >
                    <User size={16} className="text-white" />
                </button>

                {/* Logout - Icon only on mobile, button with text on desktop */}
                <button
                    onClick={logout}
                    className="sm:hidden p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Logg ut"
                >
                    <LogOut size={20} />
                </button>
                <div className="hidden sm:block">
                    <Button onClick={logout} variant="danger" size="sm">
                        Logg ut
                    </Button>
                </div>
            </div>
        </header>

        <MaintenanceAdminModal
            isOpen={showMaintenanceModal}
            onClose={() => setShowMaintenanceModal(false)}
        />
        </>
    );
};